import React, { createRef, memo, useState } from 'react';
import { Header } from '../../components/layout/header';
import { Button, Dropdown, message, Modal } from 'antd';
import { useDebounce } from '../../hook/use-debounce';
import { NonCollateralVehicleApi, OptionApi } from '../../apis';
import { Table, TableRef } from '../../components/table/table';
import { ExclamationCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { BrandFormRef } from '../../components/model/form';
import { PermissionCode, useRole } from '../../hook/use-role';
import { NoneCollateralVehicleForm } from '../../components/none-collateral-vehicle/form';

const PAGE_NAME = PermissionCode.SETTINGS_NON_COLLATERAL_VEHICLE;

const NoneCollateralVehicleList = memo(() => {
  const [value, setValue] = useState<string | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [action, setAction] = useState<any>();

  const [messageApi, contextHolderMessage] = message.useMessage();

  const tableRef = createRef<TableRef>();
  const formRef = createRef<BrandFormRef | null>();

  const [modal, contextHolder] = Modal.useModal();

  const query = useDebounce(value, 300);

  const { check } = useRole();

  const column = columns({
    options: {
      isDelete: check.isDelete(PAGE_NAME),
      isUpdate: check.isEdit(PAGE_NAME),
    },
    setType: async (type: 'delete' | 'update', id: string) => {
      console.log('type :', tableRef.current);

      switch (type) {
        case 'update':
          const res = await NonCollateralVehicleApi.get({ id: id });
          setAction(res);
          setIsModalOpen(true);
          break;
        case 'delete':
          modal.confirm({
            title: 'Устгах',
            icon: <ExclamationCircleOutlined />,
            content: 'Устгахдаа итгэлтэй байна уу!',
            okText: 'Тийм',
            cancelText: 'Үгүй',
            onOk: async () => {
              await OptionApi.remove(id);
              tableRef.current?.reload();
              messageApi.success('Амжилттай устлаа');
            },
          });
          break;
      }
    },
  });

  const onSearch = (value: any) => {
    setValue(value.target.value);
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    const res = await formRef.current?.submitForm();
    if (res) {
      onSubmit(res);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setAction(undefined);
  };

  const onSubmit = async (values: any) => {
    try {
      if (action) {
        await NonCollateralVehicleApi.update(values, action.id);

        setIsModalOpen(false);
        setAction(undefined);
        tableRef.current?.reload();

        messageApi.success('Амжилттай засварлалаа');
      } else {
        await NonCollateralVehicleApi.create(values);

        setIsModalOpen(false);

        tableRef.current?.reload();
        messageApi.success('Амжилттай');
      }
    } catch (err: any) {
      messageApi.error(err?.message);
    }
  };

  return (
    <>
      {contextHolderMessage}
      <div className="flex-col bg-white rounded-[8px] p-5">
        <Header title="Барьцаалахгүй авто машин" isGoBack={false} />
        <div className="">
          <div className="flex flex-row justify-between pb-[20px]">
            <div className="flex flex-row">
              <div className="w-[10px]" />
            </div>
            <div className="flex flex-row">
              <div className="w-[10px]" />
              {check.isCreate(PAGE_NAME) && (
                <Button type="primary" onClick={showModal} icon={<PlusCircleOutlined />}>
                  Нэмэх
                </Button>
              )}
            </div>
          </div>
          <Table
            ref={tableRef}
            columns={column}
            query={query}
            loadData={NonCollateralVehicleApi.list}
          />
        </div>
      </div>

      {isModalOpen && (
        <Modal
          title="Барьцаалахгүй машин"
          closable={{ 'aria-label': 'Custom Close Button' }}
          open={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          okText="Нэмэх"
          cancelText="Буцах"
          maskClosable={false}
        >
          <NoneCollateralVehicleForm action={action} ref={formRef} onSubmit={onSubmit} />
        </Modal>
      )}

      {contextHolder}
    </>
  );
});

const columns: any = ({
  setType,
  options,
}: {
  setType: (type: 'delete' | 'update', id: string) => void;
  options: {
    isDelete: boolean;
    isUpdate: boolean;
  };
}) => [
  {
    title: '#',
    key: 'rowIndex',
    width: 70,
    align: 'center',
    render: (_: any, __: any, index: any) => index + 1,
  },
  {
    title: 'Брэнд',
    key: 'brand',
    render: (item: any, __: any, index: any) => item?.carBrand?.name,
  },
  {
    title: 'Загвар',
    key: 'model',
    render: (item: any, __: any, index: any) => item?.carModel?.name,
  },
  {
    title: 'Улсын нэр',
    key: 'manufacturerCountry',
    render: (item: any, __: any, index: any) => item?.manufacturerCountry?.name,
  },
  {
    title: 'Хүрдний байрлал',
    key: 'steering',
    render: (item: any, __: any, index: any) => item?.steering?.name,
  },
  {
    key: 'rowIndex',
    width: 150,
    align: 'center',
    render: (item: any, _: any, index: any) => {
      if (!options?.isDelete && !options?.isUpdate) {
        return <></>;
      }

      let menus = [];

      if (options?.isUpdate === true) {
        menus.push({
          key: 'update',
          label: <span>Засах</span>,
          onClick: () => {
            console.log('item :', item);
            setType('update', item?.id!);
          },
        });
      }

      if (options?.isDelete === true) {
        menus.push({
          key: 'delete',
          label: <span className="text-red-500">Устгах</span>,
          onClick: () => setType('delete', item?.id!),
        });
      }

      return (
        <Dropdown
          trigger={['click']}
          menu={{
            items: menus,
          }}
          arrow
          placement="bottomLeft"
        >
          <Button type="default">Үйлдэл</Button>
        </Dropdown>
      );
    },
  },
];

NoneCollateralVehicleList.displayName = 'NoneCollateralVehicleList';

export { NoneCollateralVehicleList };
